import { Component, inject } from '@angular/core';
import { CommonModule, KeyValuePipe } from '@angular/common';
import { AppConfigService } from '@core/services/app-config/app-config.service';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { AlertService } from '@core/services/alert/alert.service';
import { SettingAPIService } from '@services/SettingService/setting.api.service';
import { UserLocalService } from '@services/UserService/user-local.service';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'app-debug-panel',
    standalone: true,
    imports: [CommonModule, DialogModule, KeyValuePipe, ButtonModule],
    templateUrl: './debug-panel.component.html'
})
export class DebugPanelComponent {
    private appConfigService = inject(AppConfigService);
    private appSettingsService = inject(AppSettingsService);
    private settingAPIService = inject(SettingAPIService);
    private alertService = inject(AlertService);
    private userService = inject(UserLocalService);

    visible: boolean = false;
    clearingCache: boolean = false;

    private versionClickTimestamps: number[] = [];
    private readonly CLICKS_REQUIRED: number = 8;
    private readonly CLICK_WINDOW_MS: number = 3000;

    get appVersion(): string {
        return this.appConfigService.get('version') ?? '1.0.0';
    }

    get environmentName(): string {
        return this.appConfigService.get('environmentName');
    }

    isProduction(): boolean {
        return this.appConfigService.get('production');
    }

    get appConfig() {
        return this.appConfigService.getConfig();
    }

    get appSettings() {
        return this.appSettingsService.settings();
    }

    /**
     * Settings grouped by their parent category (features are shown separately).
     * Derived from the settings object, so any new category appears automatically.
     */
    get settingGroups(): { name: string; label: string; entries: { key: string; value: unknown }[] }[] {
        const settings = this.appSettings as unknown as Record<string, unknown>;

        return Object.keys(settings)
            .filter(
                (key) =>
                    key !== 'features' && settings[key] !== null && typeof settings[key] === 'object'
            )
            .map((name) => {
                const group = settings[name] as Record<string, unknown>;
                return {
                    name,
                    label: this.prettify(name),
                    entries: Object.keys(group).map((key) => ({ key, value: group[key] }))
                };
            });
    }

    /** Turns a camelCase key into a readable label, e.g. "fundAccount" -> "Fund Account". */
    private prettify(key: string): string {
        const spaced = key.replace(/([A-Z])/g, ' $1').trim();
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    }

    /** Copies the full debug info (config + settings) to the clipboard as JSON. */
    copyDebugInfo(): void {
        const payload = {
            version: this.appVersion,
            environment: this.environmentName,
            config: this.appConfig,
            settings: this.appSettings
        };

        navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(
            () => this.alertService.showSuccess('Copied', 'Debug info copied to clipboard.'),
            () => this.alertService.showError('Copy failed. Clipboard is not available.')
        );
    }

    onClearCache(): void {
        this.clearingCache = true;
        this.settingAPIService.clearCache().subscribe({
            next: () => {
                this.alertService.showSuccess('Cache cleared', 'Laravel cache has been flushed.');
                this.clearingCache = false;
            },
            error: () => {
                this.clearingCache = false;
            }
        });
    }

    onVersionClick(): void {
        if (!this.userService.canEditableUser) return;
        const now = Date.now();
        this.versionClickTimestamps.push(now);
        this.versionClickTimestamps = this.versionClickTimestamps.filter(
            (t) => now - t <= this.CLICK_WINDOW_MS
        );
        if (this.versionClickTimestamps.length >= this.CLICKS_REQUIRED) {
            this.versionClickTimestamps = [];
            this.visible = true;
        }
    }
}
